import React, { useCallback, useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { BillingCycle, CompletionStatus, SectionName } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import { FormCurrencyInput, FormDropdown } from '@divvy-web/skylab.form'
import { DeepPartial, FormProvider, useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import AccountingSoftwareDropdown from '@sputnik/components/AccountingSoftwareDropdown'
import { getAccountingSoftwareItem } from '@sputnik/components/AccountingSoftwareDropdown'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import CreditApplicationPageLayout from '@sputnik/pages/CreditApplicationPages/CreditApplicationPageLayout'
import FormAutoSubmitProvider from '@sputnik/pages/CreditApplicationPages/FormAutoSubmit'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { hasMissingRequiredField } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import {
  businessInfo as businessInfoSchema,
  type FinanceDetailsFormValidationSchema,
  financeDetailsFormValidationSchema,
} from './financeDetailsFormValidationSchema'
import FinancialDetailsFormMarketingContent from './FinancialDetailsFormMarketingContent'
import { useGetFinancialDetailsLazyQuery } from './GetFinancialDetails.gql'
import styles from './styles.module.scss'
import { useUpdateFinancialDetails } from './UpdateFinancialDetails.gql'

const FinancialDetailsForm = () => {
  const [getClassName] = useNameFormatter('FinancialDetailsForm')
  const { appId } = useParams()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const showToast = useToast()
  const { isMajorAccountChange, isMigration } = useGetCreditAppTypes({
    salesforceCreditId: appId ?? '',
  })

  const completionStatus = useApplicationProgress({ sectionName: SectionName.FINANCE_INFO })

  /* tracking start */

  const { track } = useWebTracking()

  useEffect(() => {
    track('se_signup__application_financial_info_details__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  // query to see if we have values here
  const [getFinancialDetails, { error }] = useGetFinancialDetailsLazyQuery({
    variables: { creditApplicationId: appId ?? '' },
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load existing financial info'
          id='sputnik.FinancialDetailsForm__y9eu4c'
        />
      ),
    },
  })

  // mutation to update the form field sections here.
  const [updateFinancialDetails] = useUpdateFinancialDetails({
    variables: {
      creditApplicationId: appId ?? '',
    },
    onError: () => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to update financial info'
          id='sputnik.FinancialDetailsForm__8U74uI'
        />,
        { className: 'dd-privacy-allow' },
      )
    },
  })

  const methods = useForm<FinanceDetailsFormValidationSchema>({
    mode: 'all',
    defaultValues: async () => {
      const { data } = await getFinancialDetails()

      const { billingCycle, businessInfo, financeInfo } = data?.creditApplication ?? {
        billingCycle: null,
        businessInfo: null,
        financeInfo: null,
      }
      const { desiredCredit, expectedSpend } = financeInfo ?? {}

      return {
        businessInfo: businessInfo
          ? businessInfoSchema
              .from('accountingSoftware.accountingSoftwareKey', 'accountingSoftware')
              .cast({ ...businessInfo })
          : null,
        financeInfo: financeInfo ? { desiredCredit, expectedSpend } : null,
        billingCycle,
      }
    },
    resolver: yupResolver(financeDetailsFormValidationSchema),
  })
  const { control, reset } = methods
  const { isLoading } = useFormState({ control })

  const saveFinancialDetailsData = useCallback(
    async (data: DeepPartial<FinanceDetailsFormValidationSchema>) => {
      const { businessInfo, ...rest } = data ?? {}

      const { accountingSoftware, ...restBusinessInfo } = businessInfo ?? {}

      const input = {
        ...rest,
        ...(businessInfo && {
          businessInfo: {
            ...restBusinessInfo,
            ...(accountingSoftware && { accountingSoftware: getAccountingSoftwareItem(accountingSoftware ?? '') }),
          },
        }),
      }

      await updateFinancialDetails({
        variables: {
          creditApplicationId: appId ?? '',
          ...input,
        },
        onCompleted: (updatedData) => {
          // tracking for finance details section and finance info section as a whole
          const financialInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
            (section) =>
              section?.sectionName === SectionName.FINANCE_INFO &&
              section?.completionStatus === CompletionStatus.COMPLETED,
          )

          if (financialInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
            track('se_signup__application_financial_info_section_progress__completed', {
              is_complete: true,
            })
          }

          const {
            billingCycle: updatedBillingCycle,
            businessInfo: updatedBusinessInfo,
            financeInfo: updatedFinanceInfo,
          } = updatedData?.updateCreditApplication || {}
          const { annualRevenue, accountingSoftware } = updatedBusinessInfo || {}
          const { desiredCredit, expectedSpend } = updatedFinanceInfo || {}

          const hasMissingInfo = hasMissingRequiredField([
            annualRevenue,
            expectedSpend,
            desiredCredit,
            updatedBillingCycle,
            accountingSoftware?.accountingSoftwareKey,
          ])

          if (!hasMissingInfo) {
            track('se_signup__application_financial_info_details_section_progress__completed', {
              is_complete: true,
            })
          }
          const { billingCycle, businessInfo, financeInfo } = data

          if (billingCycle) {
            track('se_signup__application_financial_info_details_billing_cycle__submitted', {
              value_present: true,
            })
          }
          if (businessInfo?.annualRevenue) {
            track('se_signup__application_financial_info_details_revenue__submitted', {
              value_present: true,
            })
          }
          if (businessInfo?.accountingSoftware) {
            track('se_signup__application_financial_info_details_acctg_software__submitted', {
              value_present: true,
            })
          }
          if (financeInfo?.desiredCredit) {
            track('se_signup__application_financial_info_details_desired_loc__submitted', {
              value_present: true,
            })
          }
          if (financeInfo?.expectedSpend) {
            track('se_signup__application_financial_info_details_expected_spend__submitted', {
              value_present: true,
            })
          }

          reset(data)
        },
      })
    },
    [appId, completionStatus, reset, track, updateFinancialDetails],
  )

  const onNextClick = () => {
    track('se_signup__application_financial_info_details_next__fired')
    navigate(`../${PATHNAME_CONSTS.CONNECT_BANK_ACCOUNT_PATH}`)
  }

  if (isMigration || isMajorAccountChange) {
    return (
      <Navigate
        replace
        to={`../${PATHNAME_CONSTS.REVIEW_PATH}`}
      />
    )
  }

  return (
    <div className={`${styles[getClassName('')]} dd-privacy-allow`}>
      <FormProvider {...methods}>
        <div className={styles[getClassName('form')]}>
          <FormAutoSubmitProvider updateHandler={saveFinancialDetailsData} />
          <CreditApplicationPageLayout
            marketing={<FinancialDetailsFormMarketingContent />}
            pageTitle={
              <FormattedMessage
                defaultMessage='Provide a few financial details'
                id='sputnik.FinancialDetailsForm__tr3qK0'
              />
            }
            onBackClick={() => {
              track('se_signup__application_financial_info_details_back__fired')
              navigate(`../${PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH}`)
            }}
            onNextClick={onNextClick}
          >
            <div className={styles[getClassName('grid')]}>
              <FormCurrencyInput
                control={control}
                disabled={isLoading}
                label={
                  <FormattedMessage
                    defaultMessage='Annual revenue'
                    id='sputnik.FinancialDetailsForm__16D5H3'
                  />
                }
                name='businessInfo.annualRevenue'
                placeholder='$0.00'
              />
              <FormCurrencyInput
                control={control}
                disabled={isLoading}
                label={
                  <FormattedMessage
                    defaultMessage='Desired line of credit'
                    id='sputnik.FinancialDetailsForm__bUcdiS'
                  />
                }
                name='financeInfo.desiredCredit'
                placeholder='$0.00'
              />
              <FormCurrencyInput
                control={control}
                disabled={isLoading}
                label={
                  <FormattedMessage
                    defaultMessage='Expected monthly spend'
                    id='sputnik.FinancialDetailsForm__dsX8S3'
                  />
                }
                name='financeInfo.expectedSpend'
                placeholder='$0.00'
              />
              <FormDropdown
                isPortal
                className='dd-privacy-allow'
                control={control}
                disabled={isLoading}
                isSearchable={false}
                label={
                  <FormattedMessage
                    defaultMessage='Billing cycle'
                    id='sputnik.FinancialDetailsForm__Z7xDUp'
                  />
                }
                name='billingCycle'
                options={[
                  {
                    label: formatMessage({
                      defaultMessage: 'Weekly',
                      id: 'sputnik.FinancialDetailsForm__/clOBU',
                    }),
                    value: BillingCycle.WEEKLY,
                  },
                  {
                    label: formatMessage({
                      defaultMessage: 'Twice a month',
                      id: 'sputnik.FinancialDetailsForm__WJl8ur',
                    }),
                    value: BillingCycle.BI_MONTHLY,
                  },
                  {
                    label: formatMessage({
                      defaultMessage: 'Monthly',
                      id: 'sputnik.FinancialDetailsForm__wYsv4Z',
                    }),
                    value: BillingCycle.MONTHLY,
                  },
                ]}
                placeholder={
                  <FormattedMessage
                    defaultMessage='Select'
                    id='sputnik.FinancialDetailsForm__kQAf2d'
                  />
                }
              />
            </div>
            <div className={styles[getClassName('full-span')]}>
              <AccountingSoftwareDropdown<FinanceDetailsFormValidationSchema>
                control={control}
                disabled={isLoading}
                name='businessInfo.accountingSoftware'
              />
            </div>
          </CreditApplicationPageLayout>
        </div>
      </FormProvider>
    </div>
  )
}

export default FinancialDetailsForm
